import {
  collection, doc, query, where, getDocs, getDoc, setDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { addAnnotationDeletes, TITLE_ANCHOR } from '../../annotations/annotationStore';
import { byTitleOrder, followingStart } from '../../utils/timeline';

const byStart = (a, b) => a.startTime - b.startTime;

// The title's subtitle (its meaning): one per video, marked `title: true`,
// with no times. Kept out of the timed subtitles everywhere.
export const isTitleSubtitle = (s) => !!s && s.title === true;
const titleSubtitleRef = (videoId) => doc(db, 'subtitles', `${videoId}__title`);

/** The video's subtitles, by start time (not the title's). */
export async function loadSubtitles(videoId) {
  const snapshot = await getDocs(query(collection(db, 'subtitles'), where('videoId', '==', videoId)));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => !isTitleSubtitle(s)).sort(byStart);
}

/** The title's subtitle, or null. */
export async function loadTitleSubtitle(videoId) {
  const snap = await getDoc(titleSubtitleRef(videoId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Save the title's meaning: the title as its original text, the English. */
export async function saveTitleSubtitle(videoId, title, englishText) {
  const data = {
    videoId,
    title: true,
    startTime: null,
    endTime: null,
    originalText: title || '',
    englishText,
    updatedAt: serverTimestamp(),
    createdBy: 'annotator',
  };
  await setDoc(titleSubtitleRef(videoId), data, { merge: true });
  return { id: `${videoId}__title`, ...data };
}

/** The video's subtitle-free annotations (anchored to a time window), by start time. */
export async function loadTimedAnnotations(videoId) {
  const snapshot = await getDocs(query(
    collection(db, 'annotations'),
    where('videoId', '==', videoId),
    where('subtitleId', '==', null)
  ));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byStart);
}

/** The video's title annotations (TITLE_ANCHOR), in the teacher's order, else by word. */
export async function loadTitleAnnotations(videoId) {
  const snapshot = await getDocs(query(
    collection(db, 'annotations'),
    where('videoId', '==', videoId),
    where('subtitleId', '==', TITLE_ANCHOR)
  ));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byTitleOrder);
}

/**
 * Save a subtitle's edit. Annotations made in its window belong to it: when
 * its times change, those whose window equalled the old one move with it, in
 * the same batch. When its end changes, the next subtitle's start follows
 * it if they touched or would overlap (`followingStart`, Requirement 59),
 * with that subtitle's annotations; not if it would pass the next one's end.
 *
 * subtitles — the video's subtitles, to find the next one.
 * Resolves to { moved: whether annotations may have moved, neighbours:
 * the other subtitles changed, as saved }.
 */
export async function saveSubtitleEdit(subtitle, changes, subtitles = []) {
  const batch = writeBatch(db);
  batch.update(doc(db, 'subtitles', subtitle.id), changes);
  const timesChanged = subtitle.startTime !== changes.startTime || subtitle.endTime !== changes.endTime;
  const follow = followingStart(subtitle, changes, subtitles);
  const neighbours = follow && !follow.blocked ? [{ ...follow.next, startTime: follow.startTime }] : [];
  neighbours.forEach((n) => batch.update(doc(db, 'subtitles', n.id), { startTime: n.startTime }));
  if (timesChanged || neighbours.length > 0) {
    const anns = await getDocs(query(
      collection(db, 'annotations'),
      where('videoId', '==', subtitle.videoId),
      where('subtitleId', '==', null)
    ));
    const inWindow = (s) => (d) => d.data().startTime === s.startTime && d.data().endTime === s.endTime;
    if (timesChanged) {
      anns.docs.filter(inWindow(subtitle))
        .forEach((d) => batch.update(d.ref, { startTime: changes.startTime, endTime: changes.endTime }));
    }
    if (follow && neighbours.length > 0) {
      anns.docs.filter(inWindow(follow.next))
        .forEach((d) => batch.update(d.ref, { startTime: follow.startTime }));
    }
  }
  await batch.commit();
  return { moved: timesChanged || neighbours.length > 0, neighbours };
}

/** Delete a subtitle with the annotations made on its words (and their counts). */
export async function deleteSubtitle(subtitleId) {
  const annSnap = await getDocs(query(collection(db, 'annotations'), where('subtitleId', '==', subtitleId)));
  const batch = writeBatch(db);
  addAnnotationDeletes(batch, annSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  batch.delete(doc(db, 'subtitles', subtitleId));
  await batch.commit();
}
